/**
 * Bank Account Adoption diagnostic card.
 *
 * CLAUDE.md task: an RBS Open Banking provider switch (or any future
 * disconnect+reconnect / bank-forced reauth) mints a new bank_accounts doc
 * under a new provider account id, leaving the old doc (entity mapping,
 * transaction history, reconciliation state) detached from any live
 * connection. This card is the retroactive-repair half of that mechanism
 * (see services/account_adoption.py) -- the connect-time half runs inline
 * during sync and never needs this card for the common case.
 *
 * Apply only ever adopts a pair with EXACTLY ONE strong (IBAN, or unmasked
 * sort-code+account-number) match. Anything ambiguous (multiple candidates,
 * or masked-only) is listed with its full ranked candidate list for manual
 * resolution via radio button + Resolve -- same pattern as the Statement
 * Orphan Repoint card.
 *
 * Optionally scoped to one connection_id (the Integrations page's
 * per-connection "Adopt accounts" action) via the connectionId prop.
 */

import { useState } from 'react';
import { Loader2, Search, CheckCircle2, AlertTriangle, GitPullRequestArrow } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { accountAdoptionAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

var CONFIDENCE_LABEL = {
  exact_iban: 'IBAN match',
  exact_domestic: 'sort code + account number match',
  masked_domestic: 'masked sort code + account number match',
};

function AutoAdoptableRow({ pair }) {
  return (
    <tr className="border-b border-border/50">
      <td className="py-1.5 pr-3 font-mono text-xs">{pair.live_account.account_name}</td>
      <td className="py-1.5 pr-3 font-mono text-xs text-muted-foreground">{pair.live_account.id}</td>
      <td className="py-1.5 pr-3 font-mono text-xs">{pair.target_account_id}</td>
      <td className="py-1.5 pr-3 text-xs text-muted-foreground">
        {CONFIDENCE_LABEL[pair.confidence] || pair.confidence}
      </td>
    </tr>
  );
}

function CandidatePicker({ item, onResolve, resolving }) {
  var [selected, setSelected] = useState(null);
  var candidates = item.candidates || [];

  return (
    <div className="border border-amber-300 dark:border-amber-800 rounded p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-mono text-muted-foreground">
          {item.live_account.account_name} ({item.live_account.id})
        </p>
        <Badge className="bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 text-[10px]">
          ambiguous
        </Badge>
      </div>

      {candidates.length === 0 ? (
        <p className="text-xs text-muted-foreground">No candidate accounts found.</p>
      ) : (
        <>
          <div className="space-y-1">
            {candidates.map(function (c) {
              var inputId = item.live_account.id + '__' + c.id;
              return (
                <label key={c.id} htmlFor={inputId} className="flex items-center gap-2 text-xs cursor-pointer">
                  <input
                    type="radio"
                    id={inputId}
                    name={'candidate-' + item.live_account.id}
                    value={c.id}
                    checked={selected === c.id}
                    onChange={() => setSelected(c.id)}
                  />
                  <span className="font-mono">{c.id}</span>
                  <span>{c.account_name}</span>
                  <span className="text-muted-foreground">entity: {c.entity_id || 'unassigned'}</span>
                  <span className="text-muted-foreground">{CONFIDENCE_LABEL[c.confidence] || c.confidence}</span>
                </label>
              );
            })}
          </div>
          <Button
            size="sm"
            variant="outline"
            disabled={!selected || resolving}
            onClick={() => onResolve(item.live_account.id, selected)}
          >
            {resolving ? <Loader2 className="h-3 w-3 animate-spin mr-2" /> : null}
            Resolve
          </Button>
        </>
      )}
    </div>
  );
}

export function AccountAdoptionPanel({ connectionId } = {}) {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(false);
  var [applying, setApplying] = useState(false);
  var [applyResult, setApplyResult] = useState(null);
  var [resolvingId, setResolvingId] = useState(null);

  var loadPreview = async function () {
    setLoading(true);
    setApplyResult(null);
    try {
      var res = await accountAdoptionAPI.preview(connectionId);
      setData(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  var handleApply = async function () {
    setApplying(true);
    try {
      var res = await accountAdoptionAPI.apply(connectionId);
      var result = res.data;
      toast.success('Adopted ' + result.adopted_count + ' account(s).');
      setApplyResult(result);
      await loadPreview();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplying(false);
    }
  };

  var handleResolve = async function (liveAccountId, targetAccountId) {
    setResolvingId(liveAccountId);
    try {
      await accountAdoptionAPI.resolve(liveAccountId, targetAccountId);
      toast.success('Adopted ' + targetAccountId + ' into ' + liveAccountId + '.');
      await loadPreview();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setResolvingId(null);
    }
  };

  var autoAdoptableCount = data ? data.auto_adoptable_count : 0;
  var ambiguousCount = data ? data.ambiguous_count : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <GitPullRequestArrow className="h-4 w-4" />
          Account Adoption{connectionId ? ' (this connection)' : ''}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          A reconnect or provider switch (e.g. RBS Bankline -&gt; RBS Digital Banking) can leave an
          old bank account doc -- entity mapping, transaction history -- detached from any live
          connection while a new doc appears under a new provider account id. Apply only ever
          adopts a pair with exactly one strong (IBAN, or unmasked sort code + account number)
          match; every ambiguous or masked-only match is listed below for manual resolution.
          Adoption carries entity assignment across and re-points transactions, subscriptions and
          matches onto the surviving account; the old doc is marked merged and hidden, never
          deleted.
        </p>

        <div className="flex gap-2">
          <Button onClick={loadPreview} disabled={loading}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Search className="h-4 w-4 mr-2" />
            )}
            Preview
          </Button>

          {data && autoAdoptableCount > 0 && (
            <Button
              variant="destructive"
              onClick={handleApply}
              disabled={applying || loading}
            >
              {applying ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <GitPullRequestArrow className="h-4 w-4 mr-2" />
              )}
              Adopt {autoAdoptableCount} Account{autoAdoptableCount !== 1 ? 's' : ''}
            </Button>
          )}
        </div>

        {data && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm">
              {autoAdoptableCount === 0 && ambiguousCount === 0 ? (
                <>
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <span className="text-muted-foreground">No duplicate/detached accounts found</span>
                </>
              ) : (
                <>
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <span>
                    {autoAdoptableCount} auto-adoptable{ambiguousCount > 0
                      ? ', ' + ambiguousCount + ' need' + (ambiguousCount === 1 ? 's' : '') + ' manual resolution'
                      : ''}
                  </span>
                </>
              )}
            </div>

            {data.auto_adoptable && data.auto_adoptable.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="py-1.5 pr-3 font-medium">Account</th>
                      <th className="py-1.5 pr-3 font-medium">Live Id</th>
                      <th className="py-1.5 pr-3 font-medium">Adopts</th>
                      <th className="py-1.5 pr-3 font-medium">Evidence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.auto_adoptable.map(function (pair) {
                      return <AutoAdoptableRow key={pair.live_account.id} pair={pair} />;
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {data.ambiguous && data.ambiguous.length > 0 && (
              <div className="space-y-2">
                {data.ambiguous.map(function (item) {
                  return (
                    <CandidatePicker
                      key={item.live_account.id}
                      item={item}
                      onResolve={handleResolve}
                      resolving={resolvingId === item.live_account.id}
                    />
                  );
                })}
              </div>
            )}
          </div>
        )}

        {applyResult && (
          <p className="text-xs text-muted-foreground">
            Last apply: {applyResult.adopted_count} account(s) adopted.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
